import { inspectOwnedResource, notRequiredResult, unmanagedResourceError, unmanagedResourceMessage } from "./results";
import { isResourceApplicable } from "./applicability";
import { applyAdapterBindings } from "@/features/adapters/model/bindings";
import { getResourceGuidance, isGuidanceComplete } from "@/features/adapters/model/guidance";
import type { ReleaseConnection } from "@packages/commons";

import type { AdapterApplyAnswers } from "@/features/adapters/model/types";
import { findConnectionById, findConnectionByName } from "@/features/adapters/model/helpers";
import { releaseCollection } from "./collections";
import type {
  ConnectionResourceDefinition,
  ResourceApplyResult,
  ResourceDriver,
  ResourceInput,
} from "./types";

type ConnectionValues = ReleaseConnection & Record<string, unknown>;

const getConnectionInputs = (
  definition: ConnectionResourceDefinition,
  answers: AdapterApplyAnswers,
  connection: ReleaseConnection | undefined,
  connectionTested: boolean,
): ResourceInput[] => {
  const values = connection as ConnectionValues | undefined;
  const href = connection?.id
    ? `/build/connections?connectionId=${encodeURIComponent(connection.id)}`
    : undefined;

  if (definition.resource.guidance) {
    const checkedValues = { ...values, hasPassword: values?.hasPassword === true || Boolean(values?.password) || connectionTested };
    return getResourceGuidance(definition.resource.guidance, answers).map((item) => ({
      id: item.id, title: item.targetPath, instructions: item.instructions,
      complete: isGuidanceComplete(checkedValues, item), href,
    }));
  }
  return [];
};

const validateConnectionPayload = (payload: Record<string, unknown>): void => {
  if (payload.type === "sftp" && (!Number.isInteger(Number(payload.port)) || Number(payload.port) < 1 || Number(payload.port) > 65535)) {
    throw new Error("SFTP port must be between 1 and 65535.");
  }
};

export const buildConnectionPayload = (
  definition: ConnectionResourceDefinition,
  answers: AdapterApplyAnswers,
): Record<string, unknown> => {
  const connection = definition.resource;
  if (connection.data || connection.bindings) {
    const payload = applyAdapterBindings({
      name: connection.name, type: connection.type, identifier: connection.identifier,
      timeout: connection.timeout, ...connection.data,
    }, connection, answers);
    validateConnectionPayload(payload);
    return payload;
  }
  if (connection.type === "sftp") {
    const port = Number(answers.port ?? 22);
    if (!Number.isInteger(port) || port <= 0 || port > 65535) {
      throw new Error("SFTP port must be between 1 and 65535.");
    }
    return {
      name: connection.name,
      identifier: connection.identifier,
      type: "sftp",
      host: answers.host?.trim() ?? "",
      port,
      path: answers.path?.trim() ?? "",
      authType: "none",
      username: answers.username?.trim() ?? "",
      timeout: connection.timeout,
    };
  }

  const authType = answers.authType ?? "none";
  return {
    name: connection.name,
    type: connection.type,
    url: answers.baseUrl?.trim() ?? "",
    contentType: connection.contentType,
    timeout: connection.timeout,
    tlsVersion: connection.tlsVersion,
    verifyCerts: connection.verifyCerts,
    authType,
    ...(authType === "basic" ? { username: answers.username, password: answers.password } : {}),
    ...(authType === "apiKey" || authType === "bearer" ? { apiKey: answers.apiKey } : {}),
  };
};

export const connectionResourceDriver: ResourceDriver<ConnectionResourceDefinition, "connection"> = {
  kind: "connection",
  setup: "automatic",
  collection: releaseCollection<ReleaseConnection>("connections", "connections", (connection) => Boolean(connection.id && connection.name)),
  describe: (declaration) => declaration.resource.name,
  transientAnswerKeys: ["password", "apiKey"],
  persistsOwnership: true,
  define: (context, owner, declaration) => {
    const resource = declaration.resource;
    const applicable = isResourceApplicable(context.answers, owner, resource);
    return [{ key: owner.stepId, stepId: owner.stepId, definitionKey: resource.key, kind: "connection", label: resource.name, applicable, resource }];
  },
  inspect: (context, definition, resourceId) => {
    const owned = findConnectionById(context.release, resourceId);
    const conflict = owned ? undefined : findConnectionByName(context.release, definition.resource.name);
    const testResult = owned?.id && context.connectionTests
      ? Boolean(context.connectionTests[owned.id])
      : undefined;
    const tested = testResult === true;
    const inputs = getConnectionInputs(
      definition,
      context.answers,
      owned,
      tested,
    );
    const configured = Boolean(owned) && inputs.every((input) => input.complete);
    const href = owned?.id
      ? `/build/connections?connectionId=${encodeURIComponent(owned.id)}`
      : undefined;
    return inspectOwnedResource(definition, {
      found: owned,
      conflict,
      inputs,
      complete: configured && (testResult === undefined || tested),
      messages: {
        conflict: unmanagedResourceMessage(`A ${definition.resource.name} connection`),
        missing: "Create the connection with the integration defaults, then complete it on the Connections page.",
        incomplete: configured ? "The connection must pass a live test." : "Open the managed connection and complete its required fields.",
        ready: "The connection is ready.",
      },
      href,
      linkLabel: href ? "Open connection" : undefined,
      action:
        definition.applicable && !owned && !conflict
          ? { kind: "apply", label: "Create connection", stepId: definition.stepId }
          : definition.applicable && configured && testResult === false && owned?.id
            ? { kind: "testConnection", label: "Test connection", resourceId: owned.id }
            : undefined,
    });
  },
  ensure: async (context, definition, resourceId): Promise<ResourceApplyResult> => {
    if (!definition.applicable) return notRequiredResult(definition);
    const owned = findConnectionById(context.release, resourceId);
    const conflict = owned ? undefined : findConnectionByName(context.release, definition.resource.name);
    if (conflict) {
      throw unmanagedResourceError(`Connection "${definition.resource.name}"`);
    }
    if (owned) {
      const updated = applyAdapterBindings(owned as ConnectionValues, definition.resource, context.answers);
      if (JSON.stringify(updated) === JSON.stringify(owned)) return { message: `${definition.resource.name} connection already exists.` };
      validateConnectionPayload(updated);
      const release = await context.operations.updateConnection(owned.id!, updated);
      return { release, message: `Updated ${definition.resource.name} connection.` };
    }

    const release = await context.operations.createConnection(
      buildConnectionPayload(definition, { ...context.definition.defaultAnswers, ...context.answers }),
    );
    const created = findConnectionByName(release, definition.resource.name);
    if (!created?.id) throw new Error(`Could not find created connection "${definition.resource.name}".`);

    return { release, resourceId: created.id, message: `Created ${definition.resource.name} connection.` };
  },
};
